import React, {useCallback, useMemo, useRef} from 'react';
import {v4 as uuidv4} from 'uuid';

import BenchlingConfig from 'BenchlingConfig';
import {ProcedureParameterConfirmationMethod} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  CreateStructuredTableProps,
  LastSyncedUpstreamValue,
  StructuredTableColumn,
  StructuredTableRow,
} from 'notebook/structured-tables/StructuredTableConstants';
import {EMPTY_PLACEHOLDER_INSERTION_CONTEXT} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import computeLSUVs from 'procedures/design/method-config/computeLSUVs';
import {ProcedureValidationContext} from 'procedures/design/validation/ProcedureValidationContext';
import {Method, MethodUpdater, ProcedureDataParameterSetting} from 'procedures/ProcedureDomainTypes';
import {
  DataParameterType,
  DEFAULT_DATA_PARAMETER_TYPE,
} from 'procedures/shared-components/schema-field-type-selectors/procedureSchemaFieldConstants';
import {ProcedureDataParametersConfigTableMetadata} from 'procedures/tables/BaseProcedureDataParametersConfigTableAdapter';
import {transformLsuvsForRowFn} from 'procedures/tables/parameterTableAdapterParamUsageUtils';
import ProcedureDataParametersConfigTableAdapter, {
  ProcedureDataParametersConfigTableRowMetadata,
} from 'procedures/tables/ProcedureDataParametersConfigTableAdapter';
import ProcedureDataParametersConfigWithParamLibraryTableAdapter, {
  ProcedureDataParametersConfigWithParamLibraryTableRowMetadata,
} from 'procedures/tables/ProcedureDataParametersConfigWithParamLibraryTableAdapter';
import useRowDelegateController from 'procedures/tables/useRowDelegateController';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import useOnMount from 'ui-library/hooks/useOnMount';

import ProcedureConfigTable from './ProcedureConfigTable';

const baseCreateParametersTableProps: Omit<
  CreateStructuredTableProps<ProcedureDataParametersConfigTableMetadata>,
  'metadata'
> = {
  name: 'Parameters',
  schemas: [],
  entryId: null,
  selectorsById: {},
  structuredTableInsertionContext: EMPTY_PLACEHOLDER_INSERTION_CONTEXT,
};

interface DataParameterConfigTableProps {
  method: Method;
  onChangeMethod: (cid: string, methodUpdater: MethodUpdater) => void;
  onChangeErrorState: (anyErrors: boolean) => void;
  onReadyForEditing: () => void;
  locked: boolean;
  navigateToStep: (stepCid: string) => void;
}

function makeEmptyDataParameterSetting(): ProcedureDataParameterSetting {
  const cid = uuidv4();
  return {
    dataParameterVersion: {
      cid,
      id: null,
      procedureDataParameterId: null,
      name: '',
      description: '',
      dataType: DEFAULT_DATA_PARAMETER_TYPE,
      minimum: null,
      maximum: null,
      confirmationMethod: ProcedureParameterConfirmationMethod.TABLE,
      libraryParameterDefinition: null,
    },
    values: [
      {
        id: null,
        cid: `${cid}-planned-value`,
        value: {
          value: null,
          rawValue: null,
          floatValue: null,
          type: FieldType.FLOAT,
        },
      },
    ],
  };
}

type DataParamsTableRowMetadata =
  | ProcedureDataParametersConfigTableRowMetadata
  | ProcedureDataParametersConfigWithParamLibraryTableRowMetadata;

export default function DataParameterConfigTable(props: DataParameterConfigTableProps): JSX.Element {
  const {method, onChangeMethod} = props;
  const {procedureValidationErrors} = ProcedureValidationContext.useContext();
  const useLibraryAdapter = BenchlingConfig.PROCEDURES_ENABLE_PARAMETER_LIBRARY_ENFORCEMENT;
  const adapterClass = useLibraryAdapter
    ? ProcedureDataParametersConfigWithParamLibraryTableAdapter
    : ProcedureDataParametersConfigTableAdapter;
  const adapter = adapterClass.getAdapter();
  const makeLastSyncedUpstreamValues = adapterClass.makeLastSyncedUpstreamValues;
  const getDelegateColumnByColumnAndDataType = adapterClass.getDelegateColumnByColumnAndDataType;
  const rowToDataParameterSettingFn = useCallback(
    (columns: Array<StructuredTableColumn>, row: StructuredTableRow<DataParamsTableRowMetadata>) =>
      adapterClass.rowToDataParameterSetting(columns, row),
    [adapterClass]
  );

  const lsuvs = useRef<Record<string, Array<LastSyncedUpstreamValue>> | null>(null);
  const createParametersTableProps = useMemo(
    () => ({...baseCreateParametersTableProps, metadata: {navigateToStep: props.navigateToStep}}),
    [props.navigateToStep]
  );

  const rowDelegateController = useRowDelegateController<
    ProcedureDataParameterSetting,
    ProcedureDataParametersConfigTableMetadata,
    DataParamsTableRowMetadata,
    DataParameterType
  >({
    dependentColumnProvenanceIds: 'ALL_DELEGATE',
    details: method.dataParameterSettings,
    getDataTypeByIndex: (index) =>
      method.dataParameterSettings[index]?.dataParameterVersion.dataType ?? DEFAULT_DATA_PARAMETER_TYPE,
    createTable: (installDelegatesIntoRow) => {
      const dataParameterSettings =
        method.dataParameterSettings.length > 0
          ? method.dataParameterSettings
          : [makeEmptyDataParameterSetting()];

      const table = adapter.createTableFromDataParameters(
        createParametersTableProps,
        `procedureDataParamTable-${method.cid}`,
        dataParameterSettings,
        procedureValidationErrors,
        method.parameterUsages
      );

      const rowsWithDelegates = table.rows.map(
        (row: StructuredTableRow<DataParamsTableRowMetadata>, index: number) =>
          installDelegatesIntoRow(table, row, index)
      );

      if (lsuvs.current == null) {
        lsuvs.current = computeLSUVs(
          rowsWithDelegates,
          table.columns,
          dataParameterSettings,
          (p) => p.dataParameterVersion.id,
          transformLsuvsForRowFn(
            makeLastSyncedUpstreamValues,
            method.parameterUsages,
            (model) => model.dataParameterVersion.id
          )
        );
      }

      return {
        ...table,
        rows: table.rows.map((row: StructuredTableRow<DataParamsTableRowMetadata>, index: number) => {
          const dataParam = dataParameterSettings[index];
          const id = dataParam.dataParameterVersion.id;
          return {
            ...rowsWithDelegates[index],
            ...(id && lsuvs.current![id] ? {lastSyncedUpstreamValues: lsuvs.current![id]} : null),
            isFinalized: id != null,
          };
        }),
      };
    },
    getDelegateColumnByColumnAndDataType,
  });

  const onDataParameterSettingsChangeDetails = useCallback(
    (dataParameterSettings: Array<ProcedureDataParameterSetting>) => {
      onChangeMethod(method.cid, (m) => ({...m, dataParameterSettings}));
      // The parent needs to flush before the row delegates can be refreshed
      setTimeout(() => {
        rowDelegateController.onChangeDetails(dataParameterSettings);
      }, 0);
    },
    [method, onChangeMethod, rowDelegateController]
  );

  // This table is ready immediately
  useOnMount(props.onReadyForEditing);

  return (
    <ProcedureConfigTable
      tableAdapter={adapter}
      onChangeDetails={onDataParameterSettingsChangeDetails}
      onChangeErrorState={props.onChangeErrorState}
      rowToDetail={(row, columns) =>
        rowToDataParameterSettingFn(columns, row as StructuredTableRow<DataParamsTableRowMetadata>)
      }
      table={rowDelegateController.table}
      tableCid={`procedureDataParamTable-${method.cid}`}
      locked={props.locked}
      dataTestAttrs={{component: 'DataParameterConfigTable', element: 'root'}}
    />
  );
}
